import { useCallback, useEffect, useRef, useState } from "react";
import Icon from "./Icon";

// Confirmacion DENTRO de la aplicacion, en lugar de window.confirm().
//
// El dialogo del navegador tiene tres problemas que no son de estetica:
//
//   · Sale con la cabecera "manager.angellinares.es dice", con los botones del
//     sistema y con la tipografia del sistema. No parece de la aplicacion:
//     parece que algo ha ido mal.
//   · Es modal del NAVEGADOR: bloquea el hilo, tapa la pantalla y no deja ver
//     de que esta hablando. Justo cuando lo que hace falta es mirar lo que vas
//     a borrar.
//   · Solo se sale por sus dos botones. Pulsar fuera —el gesto natural para
//     "dejalo"— no hace nada.
//
// Este resuelve los tres: se pinta con los colores y la fuente de la casa,
// deja ver el fondo, y pulsar fuera CANCELA. Y quien lo abre puede antes
// centrar la vista en el elemento del que se habla (ver `useConfirmar`).

export default function Confirmar({
  titulo, mensaje, detalle, etiquetaOk = "Aceptar", peligro = false, onCerrar,
}) {
  const okRef = useRef(null);
  const cancelarRef = useRef(null);

  // En una accion destructiva el foco va a Cancelar: un Enter reflejo no debe
  // borrar. En el resto va a confirmar, para que Enter y Espacio hagan lo evidente.
  useEffect(() => { (peligro ? cancelarRef : okRef).current?.focus(); }, [peligro]);

  // En CAPTURA y sobre window, no en el propio dialogo. Asi Escape no llega a
  // los atajos del lienzo, que tambien escuchan en window: sin esto, Escape
  // cerraba el dialogo Y limpiaba la seleccion, y al cancelar te quedabas sin
  // saber de que iba.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onCerrar(false); }
      if (e.key === "Enter") {
        // Enter solo confirma si el foco esta en el boton de confirmar, o en el
        // cuerpo del dialogo cuando la accion no es peligrosa. Si el foco esta en
        // Cancelar, Enter lo activa a el.
        const enCancelar = document.activeElement === cancelarRef.current;
        if (enCancelar || (peligro && document.activeElement !== okRef.current)) return;
        e.preventDefault(); e.stopPropagation(); onCerrar(true);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCerrar, peligro]);

  return (
    <div
      onPointerDown={e => { if (e.target === e.currentTarget) onCerrar(false); }}
      style={{
        position: "absolute", inset: 0, zIndex: 40, display: "flex",
        alignItems: "center", justifyContent: "center", padding: 20,
        // Un velo claro, no un apagon: lo que se esta confirmando esta detras y
        // hay que poder verlo. Es la razon de ser de este dialogo.
        background: "rgba(15,23,42,.20)", animation: "fadeIn .12s ease both",
      }}>
      <div role="dialog" aria-modal="true" aria-label={titulo}
        style={{
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 14, boxShadow: "var(--shadow-lg)", padding: 18,
          width: "100%", maxWidth: 400, animation: "scaleIn .14s ease both",
        }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
          <div style={{
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            // Rojo translucido y no un literal claro: el mismo recuadro tiene
            // que funcionar sobre el tema oscuro. --red-bg no existe como
            // token.
            background: peligro ? "rgba(239,68,68,.12)" : "var(--accent-bg)",
          }}>
            {/* trash3 y exclamation-triangle-fill, que son los nombres que SI
                estan en ICONS. Icon.jsx hace `if (!path) return null`: un
                nombre inventado no da error, deja un hueco invisible. */}
            <Icon name={peligro ? "trash3" : "exclamation-triangle-fill"} size={14}
              color={peligro ? "var(--red-text)" : "var(--accent)"} />
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text)", lineHeight: 1.4 }}>
              {titulo}
            </div>
            {mensaje && (
              <div style={{ fontSize: 12.5, color: "var(--text2)", lineHeight: 1.6, marginTop: 4 }}>
                {mensaje}
              </div>
            )}
          </div>
        </div>

        {/* El detalle es lo que se lleva por delante: nombres, cuantas
            conexiones, que se pierde. Va aparte del mensaje porque es una lista
            y porque es lo que de verdad decide. */}
        {detalle && (
          <div style={{
            fontSize: 11.5, color: "var(--text3)", lineHeight: 1.6,
            background: "var(--bg)", border: "1px solid var(--border2)",
            borderRadius: 8, padding: "7px 10px", marginBottom: 12,
            maxHeight: 150, overflowY: "auto",
          }}>
            {detalle}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button ref={cancelarRef} className="btn" onClick={() => onCerrar(false)}
            style={{ padding: "7px 13px", borderRadius: 8, border: "1px solid var(--border)",
              background: "var(--surface)", color: "var(--text2)", cursor: "pointer",
              fontFamily: "var(--font)", fontSize: 12.5, fontWeight: 600 }}>
            Cancelar
          </button>
          <button ref={okRef} onClick={() => onCerrar(true)}
            style={{ padding: "7px 13px", borderRadius: 8, border: "none",
              background: peligro ? "var(--danger)" : "var(--accent)", color: peligro ? "var(--on-danger)" : "var(--on-accent)",
              cursor: "pointer", fontFamily: "var(--font)", fontSize: 12.5, fontWeight: 700 }}>
            {etiquetaOk}
          </button>
        </div>
      </div>
    </div>
  );
}

// El dialogo, con una espera que se puede usar como se usaba window.confirm:
//
//   const [pedir, dialogo] = useConfirmar();
//   ...
//   if (!await pedir({ titulo: "¿Eliminar?", peligro: true })) return;
//   ...
//   return <div>{dialogo}</div>;
//
// Devuelve una promesa que se resuelve a true o false, asi que el sitio que lo
// llama conserva la forma que tenia y no hay que partir cada manejador en dos
// mitades con un estado "pendiente" en medio.
//
// `antes` se ejecuta justo antes de pintar el dialogo: es donde el lienzo
// centra la vista en el elemento del que se va a hablar.
export function useConfirmar() {
  const [opciones, setOpciones] = useState(null);
  // La funcion que resuelve la promesa vive en un ref y no en el estado: si
  // viviera en el estado, cerrar el dialogo dentro del actualizador seria un
  // efecto secundario dentro de un reducer, y React lo llama dos veces en modo
  // estricto —resolviendo la promesa dos veces—.
  const resolver = useRef(null);

  const pedir = useCallback((op = {}) => {
    op.antes?.();
    return new Promise(res => { resolver.current = res; setOpciones(op); });
  }, []);

  const cerrar = useCallback((valor) => {
    const res = resolver.current;
    resolver.current = null;
    setOpciones(null);
    res?.(valor);
  }, []);

  // Si el componente se desmonta con el dialogo abierto, la promesa se resuelve
  // a false en vez de quedarse colgada para siempre.
  useEffect(() => () => resolver.current?.(false), []);

  const dialogo = opciones ? <Confirmar {...opciones} onCerrar={cerrar} /> : null;
  return [pedir, dialogo, !!opciones];
}
